import { useState, useEffect, useRef } from 'react';
import { MdNotifications, MdNotificationsActive, MdDoneAll, MdAdminPanelSettings, MdPerson, MdClose, MdComment, MdTask, MdPeople } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import socket from '../../services/socket';
import { formatDistanceToNow } from 'date-fns';
import './NotificationBell.css';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef();
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.data);
      setUnread(data.unreadCount);
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();

    // Real-time new notification
    socket.on('newNotification', (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnread((prev) => prev + 1);
    });

    // Close panel on outside click
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      socket.off('newNotification');
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications((prev) => prev.map((n) => n._id === id ? { ...n, read: true } : n));
    setUnread((prev) => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`);
    const wasUnread = notifications.find((n) => n._id === id && !n.read);
    setNotifications((prev) => prev.filter((n) => n._id !== id));
    if (wasUnread) setUnread((prev) => Math.max(0, prev - 1));
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) await markRead(notif._id);
    setOpen(false);
    if (notif.task?._id) {
      navigate(`/tasks/${notif.task._id}`);
    } else {
      navigate('/tasks');
    }
  };

  const getIcon = (type) => {
    if (type === 'task_assigned') return <MdTask size={16} color="#10b981" />;
    if (type === 'co_assigned') return <MdPeople size={16} color="#06b6d4" />;
    if (type === 'admin_comment') return <MdAdminPanelSettings size={16} color="#6366f1" />;
    if (type === 'mention') return <MdPerson size={16} color="#8b5cf6" />;
    return <MdPerson size={16} color="#f59e0b" />;
  };

  const getMessage = (notif) => {
    if (notif.type === 'task_assigned') return 'assigned you a task';
    if (notif.type === 'co_assigned') return 'added you as co-assignee on a task';
    if (notif.type === 'admin_comment') return 'commented on your task';
    if (notif.type === 'mention') return 'mentioned you in a task';
    return 'replied to your comment';
  };

  const unreadCommentCount = notifications.filter((n) => !n.read && (n.type === 'admin_comment' || n.type === 'user_reply' || n.type === 'mention')).length;
  const hasCommentNotifications = unreadCommentCount > 0;

  return (
    <div className="notif-wrapper" ref={panelRef}>
      <button
        className={`btn-icon notif-btn ${unread > 0 ? 'has-unread' : ''} ${hasCommentNotifications ? 'has-comment' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={hasCommentNotifications ? `You have ${unreadCommentCount} new comment(s) on tasks` : 'Notifications'}
      >
        {hasCommentNotifications ? (
          <MdComment size={22} className="notif-comment-icon" />
        ) : unread > 0 ? (
          <MdNotificationsActive size={22} />
        ) : (
          <MdNotifications size={22} />
        )}
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <span>Notifications</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {unread > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={markAllRead}>
                  <MdDoneAll size={14} /> Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <MdNotifications size={36} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif._id}
                  className={`notif-item ${!notif.read ? 'unread' : ''}`}
                  onClick={() => handleNotifClick(notif)}
                >
                  <div className="notif-icon">{getIcon(notif.type)}</div>
                  <div className="notif-content">
                    <div className="notif-message">
                      <strong>{notif.sender?.name}</strong> {getMessage(notif)}
                    </div>
                    <div className="notif-task">📋 {notif.task?.title}</div>
                    {notif.commentText && (
                      <div className="notif-comment">"{notif.commentText.length > 60 ? notif.commentText.slice(0, 60) + '…' : notif.commentText}"</div>
                    )}
                    <div className="notif-time">{formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}</div>
                  </div>
                  <button className="notif-delete" onClick={(e) => deleteNotif(e, notif._id)}>
                    <MdClose size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
